import "@/styles/application.css"

import { createInertiaApp } from "@inertiajs/react"
import { createRoot } from "react-dom/client"
import pages from "virtual:pages"
import type { ReactNode } from "react"

// The Inertia boot, shared by the entrypoints. There are two of them because
// there are two audiences in one deployment: the signed-in app, which carries
// the copilot, and the marketing site, which must not -- the copilot's chat is
// ~1.6 MB and no anonymous visitor to "/" should wait on it. The ONLY
// difference between them is `wrap`, so everything else lives here.
//
// Which entry a page gets is the server's call: routes/_layout.rb renders
// `vite_javascript_tag entrypoint`, and routes/(web)/_layout.rb overrides that
// helper for the marketing group.

// Every _layout.rb on the way down to the page wraps it, outermost first.
const layoutsFor = (name: string) => {
  const segments = name.split("/")
  segments.pop()
  return segments
    .map((_, index) => [...segments.slice(0, index), "_layout"].join("/"))
    .concat([...segments, "_layout"].join("/"))
    .filter((layout) => layout in pages)
}

// `wrap` receives <App /> and the initial page props, and returns what gets
// rendered -- which is how the app entry puts the copilot provider ABOVE every
// page (the only place tools and context can be registered from).
export function mountInertia(
  wrap: (app: ReactNode, pageProps: Record<string, unknown>) => ReactNode = (app) => app,
) {
  return createInertiaApp({
    resolve: async (name) => {
      const page = pages[name]
      if (!page) throw new Error(`No __END__ template in routes/${name}.rb`)
      const module = await page()

      const layouts = await Promise.all(
        layoutsFor(name).map(async (layout) => (await pages[layout]!()).default),
      )

      if (layouts.length && module.default.layout === undefined) {
        module.default.layout = (child: ReactNode) =>
          layouts.reduceRight((inner, Layout) => <Layout>{inner}</Layout>, child)
      }

      return module
    },
    setup: ({ el, App, props }) =>
      createRoot(el).render(wrap(<App {...props} />, props.initialPage.props)),
  })
}
